/**
 * Thumbnail Generation Hook
 * The picture of the canvas a workspace gets in the library, taken when it is left.
 *
 * Leaving shows a loading screen, and that screen covers the canvas. So there are two steps:
 * 1. `pictureBeforeCover`, before the loading screen goes up: in the desktop app the shell
 *    takes the picture, outside the page (a short wait at most). In a browser there is
 *    nothing to wait for, and it gives null at once.
 * 2. `generateThumbnail`, once the loading screen is up: a picture already made (the
 *    workspace's cover, its newest generated picture, from utils/workspaceCover.ts; else the
 *    shell's picture), else one drawn in the page from the canvas still mounted under the
 *    screen (a few milliseconds), else a placeholder with the workspace's name.
 */

import { useCallback, useRef } from 'react';
import { captureCanvasByShell, drawCanvasThumbnail, generatePlaceholderThumbnail, shellCanCapture, type CoveredEdges } from '../utils/thumbnailGenerator';

export function useThumbnailGenerator() {
  // The picture each workspace was last given in this session: the same one is not sent twice
  const givenRef = useRef(new Map<string, string>());

  const pictureBeforeCover = useCallback(
    (container: HTMLElement | null, covered?: CoveredEdges): Promise<string | undefined> | null =>
      (shellCanCapture() ? captureCanvasByShell(container, covered) : null),
    [],
  );

  const generateThumbnail = useCallback(
    (container: HTMLElement | null, workspaceName: string, ready?: string, covered?: CoveredEdges): string | undefined => {
      try {
        return ready || drawCanvasThumbnail(container, covered) || generatePlaceholderThumbnail(workspaceName) || undefined;
      } catch (error) {
        console.error('Error generating thumbnail:', error);
        return undefined;
      }
    },
    []
  );

  /**
   * The thumbnail to send with a save of `workspaceId`: none when the workspace already has
   * this very picture (`known` is the one the library lists for it).
   */
  const thumbnailToSave = useCallback((workspaceId: string, thumbnail: string | undefined, known?: string): string | undefined => {
    if (!thumbnail) return undefined;
    if (thumbnail === (givenRef.current.get(workspaceId) ?? known)) return undefined;
    givenRef.current.set(workspaceId, thumbnail);
    return thumbnail;
  }, []);

  /** The save that carried `workspaceId`'s thumbnail did not get through: the next one sends it again. */
  const forgetThumbnail = useCallback((workspaceId: string) => {
    givenRef.current.delete(workspaceId);
  }, []);

  return {
    pictureBeforeCover,
    generateThumbnail,
    thumbnailToSave,
    forgetThumbnail,
  };
}
